import { Fragment, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useProgram } from './ProgramContext';
import { Reveal, TrainerNote, CodedImage } from './components';
import { BottomDock } from './Shell';
import { AircraftFleetGallery } from './AircraftIllustrations';
import { CrisisDeck } from './CrisisDeck';

// ============================================================
// Code-block detection: [GATE33-DXX-MYY-LABEL-IMG-NN]
// A line that is ONLY a code becomes a block-level image/placeholder.
// ============================================================
const CODE_LINE_RE = /^\s*(\[GATE33-[A-Z0-9-]+-IMG-\d{2,3}\])\s*$/;
const CODE_ANY_RE = /(\[GATE33-[A-Z0-9-]+-IMG-\d{2,3}\])/g;
const CODE_EXACT_RE = /^\[GATE33-[A-Z0-9-]+-IMG-\d{2,3}\]$/;

// Put every image code on its own line so it renders as a block image
const isolateCodes = (text) => String(text).replace(/[ \t]*(\[GATE33-[A-Z0-9-]+-IMG-\d{2,3}\])[ \t]*/g, '\n$1\n');

// Render plain-text fields with image codes resolved (raw code shown when no image uploaded)
const withCodes = (text, render) => String(text || '').split(CODE_ANY_RE).map((seg, i) => {
  if (CODE_EXACT_RE.test(seg)) return <CodedImage key={i} code={seg} missing="raw" />;
  const t = seg.trim();
  return t ? <Fragment key={i}>{render(t, i)}</Fragment> : null;
});

// ============================================================
// Markdown-ish renderer for the trainer narrative
// ============================================================
const renderInline = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    if (p.startsWith('*') && p.endsWith('*') && p.length > 2) {
      return <em key={i}>{p.slice(1, -1)}</em>;
    }
    return <span key={i}>{p}</span>;
  });
};

// Detect a leading "**Subheading text**" that acts as a section title within a long block.
// Example patterns: **Part 1 — Cabin reset**, **Demo 1 — FBO**, **Stage 2 — comparison.**,
// **Brief:**, **Expected behaviour:**, **The principle:**, **What the pair should do:**
const SUBHEAD_RE = /^\*\*([^*]+?)\*\*(.*)$/;
const isSubheadLeader = (s) => {
  const m = s.match(SUBHEAD_RE);
  if (!m) return null;
  const head = m[1].trim();
  const rest = m[2].trim();
  if (!rest) return head; // pure subhead line
  // If rest is short and starts with a sentence completing the cue, treat the whole thing as a subhead
  if (/^(Part|Stage|Demo|Scenario|Step|Phase)\s+\d/i.test(head)) return `${head} ${rest}`.trim();
  // Bold leader followed only by italic parenthetical/qualifier (e.g. "**Management** *(including BBJ / ACJ)*")
  if (/^\*[^*]+\*$/.test(rest)) return `**${head}** ${rest}`;
  return null;
};

const renderRichText = (text, opts = {}) => {
  if (!text) return null;
  const { baseFontSize = 17, italic = false } = opts;
  const blocks = [];
  let buffer = [];
  let listBuffer = [];
  let listType = null; // 'ul' | 'ol'

  const flushBuffer = () => {
    if (!buffer.length) return;
    const para = buffer.join('\n');
    if (para.trim()) blocks.push({ type: 'p', content: para });
    buffer = [];
  };
  const flushList = () => {
    if (listBuffer.length) {
      blocks.push({ type: listType, items: listBuffer });
      listBuffer = [];
      listType = null;
    }
  };

  const lines = isolateCodes(text).split('\n');
  let noteBuf = null; // { title, body: [] }
  let rowBuf = null; // { codes: [] }
  for (const raw of lines) {
    const line = raw.trimEnd();

    // ----- Inline ROW block parsing (group of coded images, displayed side-by-side same height) -----
    if (rowBuf) {
      if (line.trim() === ':::END') {
        flushBuffer(); flushList();
        if (rowBuf.codes.length) blocks.push({ type: 'image-row', codes: rowBuf.codes, cols: rowBuf.cols });
        rowBuf = null;
      } else {
        const cm = line.match(CODE_LINE_RE);
        if (cm) rowBuf.codes.push(cm[1]);
      }
      continue;
    }
    if (line.trim() === ':::ROW' || line.startsWith(':::ROW ')) {
      flushBuffer(); flushList();
      // Parse optional cols=N parameter
      const m = line.match(/cols=(\d+)/);
      const cols = m ? parseInt(m[1], 10) : null;
      rowBuf = { codes: [], cols };
      continue;
    }

    // ----- Crisis Deck directive -----
    if (line.trim() === ':::CRISIS_DECK:::') {
      flushBuffer(); flushList();
      blocks.push({ type: 'crisis-deck' });
      continue;
    }

    // ----- Inline NOTE block parsing -----
    if (noteBuf) {
      if (line.trim() === ':::END') {
        flushBuffer(); flushList();
        blocks.push({
          type: 'inline-note',
          title: noteBuf.title,
          body: noteBuf.body.join('\n'),
        });
        noteBuf = null;
      } else {
        noteBuf.body.push(line);
      }
      continue;
    }
    const noteOpen = line.match(/^:::NOTE\s+title="([^"]+)"\s*$/);
    if (noteOpen) {
      flushBuffer(); flushList();
      noteBuf = { title: noteOpen[1], body: [] };
      continue;
    }

    const codeMatch = line.match(CODE_LINE_RE);
    if (codeMatch) {
      flushBuffer(); flushList();
      blocks.push({ type: 'coded-image', code: codeMatch[1] });
      continue;
    }
    if (line.startsWith('## ')) {
      flushBuffer(); flushList();
      blocks.push({ type: 'h3', content: line.slice(3) });
    } else if (line.startsWith('### ')) {
      flushBuffer(); flushList();
      blocks.push({ type: 'h4', content: line.slice(4) });
    } else if (line.startsWith('› ') || line.startsWith('- ') || line.startsWith('• ')) {
      flushBuffer();
      if (listType && listType !== 'ul') flushList();
      listType = 'ul';
      listBuffer.push(line.slice(2));
    } else if (/^\d+\.\s/.test(line)) {
      flushBuffer();
      if (listType && listType !== 'ol') flushList();
      listType = 'ol';
      listBuffer.push(line.replace(/^\d+\.\s/, ''));
    } else if (line.trim() === '') {
      flushBuffer(); flushList();
    } else {
      flushList();
      // Detect subheading-style bold leader on its own line (no buffered text yet)
      if (!buffer.length) {
        const sub = isSubheadLeader(line);
        if (sub) {
          blocks.push({ type: 'h4', content: sub });
          continue;
        }
      }
      buffer.push(line);
    }
  }
  flushBuffer(); flushList();

  return blocks.map((b, i) => {
    if (b.type === 'coded-image') {
      return <CodedImage key={i} code={b.code} missing="raw" />;
    }
    if (b.type === 'crisis-deck') {
      return <CrisisDeck key={i} />;
    }
    if (b.type === 'image-row') {
      const cols = b.cols || b.codes.length;
      return (
        <div
          key={i}
          data-testid={`image-row-${i}`}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            gap: 12,
            margin: '32px 0',
            alignItems: 'start',
          }}
        >
          {b.codes.map((c, ci) => (
            <CodedImage key={`${c}-${ci}`} code={c} inRow missing="raw" />
          ))}
        </div>
      );
    }
    if (b.type === 'inline-note') {
      return (
        <div key={i} style={{ margin: '24px 0 40px' }}>
          <TrainerNote
            testId={`inline-note-${i}`}
            note={{ label: 'NOTE', eyebrow: 'Trainer commentary', title: b.title, body: b.body }}
          />
        </div>
      );
    }
    if (b.type === 'h3') {
      return (
        <h3 key={i} className="g33-display" style={{
          fontSize: 'clamp(22px, 2.4vw, 30px)', marginTop: 56, marginBottom: 24,
        }}>
          {renderInline(b.content)}
        </h3>
      );
    }
    if (b.type === 'h4') {
      return (
        <h4 key={i} className="g33-eyebrow" style={{
          fontSize: 13,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--g33-or-deep)',
          marginTop: 40,
          marginBottom: 14,
          fontWeight: 600,
        }}>
          {renderInline(b.content)}
        </h4>
      );
    }
    if (b.type === 'ul' || b.type === 'ol') {
      const Tag = b.type;
      return (
        <Tag key={i} style={{
          listStyle: 'none', padding: 0, margin: '20px 0',
          counterReset: b.type === 'ol' ? 'g33-ol' : undefined,
        }}>
          {b.items.map((it, j) => (
            <li key={j} style={{
              display: 'flex', gap: 14, padding: '6px 0',
              fontSize: baseFontSize, lineHeight: 1.7, color: 'var(--g33-charcoal)',
              counterIncrement: b.type === 'ol' ? 'g33-ol' : undefined,
            }}>
              <span style={{
                color: 'var(--g33-or)', flexShrink: 0,
                fontVariantNumeric: 'tabular-nums',
                minWidth: b.type === 'ol' ? 22 : 'auto',
              }}>
                {b.type === 'ol' ? `${j + 1}.` : '›'}
              </span>
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </Tag>
      );
    }
    return (
      <p key={i} className="g33-body" style={{
        marginBottom: 20,
        whiteSpace: 'pre-line',
        fontSize: baseFontSize,
        lineHeight: 1.7,
        fontStyle: italic ? 'italic' : 'normal',
        fontFamily: italic ? 'var(--g33-font-display)' : undefined,
      }}>
        {renderInline(b.content)}
      </p>
    );
  });
};

const renderNarrative = (text) => renderRichText(text, { baseFontSize: 17 });

const scenarioStyle = { fontFamily: 'var(--g33-font-display)', fontStyle: 'italic', color: 'var(--g33-noir)' };

const renderScenario = (scn) => {
  const isShort = scn.length < 240 && !scn.includes('\n\n');
  if (isShort) {
    return (
      <p style={{ ...scenarioStyle, fontSize: 'clamp(20px, 2.4vw, 28px)', lineHeight: 1.4, margin: '0 0 24px' }}>
        {renderInline(scn)}
      </p>
    );
  }
  return scn.split(/\n\n+/).map((para, pi) => (
    <p key={pi} style={{
      ...scenarioStyle, fontSize: 'clamp(17px, 1.6vw, 20px)', lineHeight: 1.55,
      margin: '0 0 18px', whiteSpace: 'pre-line',
    }}>
      {renderInline(para)}
    </p>
  ));
};

const ReadingList = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <section style={{ padding: '0 32px 120px', maxWidth: 1080, margin: '0 auto' }} data-testid="reading-list">
      <Reveal>
        <div className="g33-eyebrow" style={{ marginBottom: 24 }}>Reading list</div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxWidth: 760 }}>
          {items.map((r, i) => (
            <li key={i} data-testid={`reading-item-${i}`} style={{ padding: '14px 0', borderBottom: '1px solid var(--g33-ligne)' }}>
              <div className="g33-display" style={{ fontSize: 20, lineHeight: 1.3 }}>{r.title}</div>
              {r.annotation && (
                <p className="g33-body" style={{ margin: '6px 0 0', fontSize: 15, color: 'var(--g33-charcoal)' }}>{r.annotation}</p>
              )}
            </li>
          ))}
        </ul>
      </Reveal>
    </section>
  );
};

// ============================================================
// Curated hero images per module — premium private aviation photography
// ============================================================
const heroCodeFor = (m) => {
  // Each module has a unique hero code [GATE33-DXX-MYY-HERO-IMG-01]
  // Uploading an image to this code will replace the hero placeholder.
  const day = String(m.day || 1).padStart(2, '0');
  const mod = String(m.module_number || 1).padStart(2, '0');
  return `[GATE33-D${day}-M${mod}-HERO-IMG-01]`;
};

const heroImageFor = (m) => {
  if (m.hero_image) return m.hero_image;
  // Curated FREE Unsplash photography (no watermark) — private aviation focus.
  // Each module has a unique image. User can override per-module via the chat.
  const map = {
    // Day 1 — Market reality, structure & positioning
    'd1-m1-context-market-reality':       'photo-1474302770737-173ee21bab63', // Gulfstream sunset on tarmac
    'd1-m2-operator-landscape':           'photo-1684838200888-192e2a163cc9', // small white jet on tarmac
    'd1-m3-cabin-crew-structures':        'photo-1720511580427-b12ed27b2e82', // person in front of private jet (crew)
    'd1-m4-codes-cultural-intelligence':  'photo-1644945082110-eaeb76f03129', // professional with bag, jet
    'd1-m5-human-factors-crm':            'photo-1619659085985-f51a00f0160a', // jet on airport
    'd1-m6-market-positioning':           'photo-1635672033267-41e2d1cb74b7', // white jet on tarmac
    // Day 2 — Service & operations
    'd2-m7-plating-presentation':         'photo-1661954864180-e61dea14208a', // table with food and wine in cabin
    'd2-m8-service-techniques':           'photo-1666307536243-a9bf2d66c51d', // table with food and glasses
    'd2-m9-catering-intelligence':        'photo-1559339352-11d035aa65de',    // catering plates editorial
    'd2-m10-cabin-logistics':             'photo-1619652116813-98504fce82d2', // jet on airport
    // Day 3 — Aircraft & interview
    'd3-m11-aircraft-familiarisation':    'photo-1619659085888-f76fd69d243a', // jet on airport
    'd3-m12-full-flight-simulation':      'photo-1587158980328-814dd4703cda', // large jet on tarmac
    'd3-m13-crisis-management':           'photo-1582744345075-cb3b8bce44ff', // jet in sky
    'd3-m14-interview-framework':         'photo-1616193572425-fd11332ec645', // engine / propeller detail
  };
  const id = map[m.slug] || 'photo-1474302770737-173ee21bab63';
  return `https://images.unsplash.com/${id}?w=2000&q=85&auto=format&fit=crop`;
};

// ============================================================
// Trainer-voice section — distinct visual treatment
// (used for Objective, Demonstration, Operational insight, Trainer narrative…)
// Dark cream backdrop with a "TRAINER VOICE · NICOLAS" eyebrow tag.
// ============================================================
const TrainerSection = ({ eyebrow, children, tone = 'default', testId }) => {
  const isDark = tone === 'dark';
  return (
    <div
      className={`g33-trainer-block ${isDark ? 'g33-trainer-block--dark' : ''}`}
      data-testid={testId}
    >
      <div className="g33-trainer-tag">
        <span className="g33-trainer-tag-dot" aria-hidden="true" />
        Trainer voice · Nicolas
      </div>
      <div className="g33-trainer-eyebrow">
        {eyebrow}
      </div>
      <div className="g33-trainer-content">
        {children}
      </div>
    </div>
  );
};

// ============================================================
// MODULE PAGE
// ============================================================
export const ModulePage = () => {
  const { slug } = useParams();
  const { modules, images } = useProgram();
  const allSorted = [...modules].sort((a, b) => (a.day - b.day) || (a.order - b.order));
  const m = allSorted.find(x => x.slug === slug);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [slug]);

  if (!m) {
    return (
      <section style={{ padding: '160px 32px', textAlign: 'center', maxWidth: 800, margin: '0 auto' }}>
        <p className="g33-eyebrow">Module not found</p>
        <Link to="/gate33-program" className="g33-btn-ghost" style={{ marginTop: 24 }}>← Back to program</Link>
      </section>
    );
  }

  // Hero image priority: uploaded coded image → curated default
  const heroCode = heroCodeFor(m);
  const uploaded = images?.[heroCode];
  const API = process.env.REACT_APP_BACKEND_URL;
  const heroUrl = uploaded
    ? (uploaded.url.startsWith('/') ? `${API}${uploaded.url}` : uploaded.url)
    : heroImageFor(m);
  const dayLabels = {
    1: 'Market Reality, Structure & Positioning',
    2: 'Catering, Galley & Cabin Operations',
    3: 'Aircraft, Simulation, Interview & Final Test',
  };

  return (
    <>
      {/* HERO — gate33.co-style split layout (image left + content right) */}
      <section className="g33-module-hero">
        <div className="g33-module-hero-grid">
          {/* IMAGE — left column, vertical full-bleed */}
          <Reveal y={32}>
            <div className="g33-module-hero-image" style={{ position: 'relative' }}>
              <motion.img
                src={heroUrl}
                alt={m.title}
                initial={{ scale: 1.06 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, ease: [0.2, 0.8, 0.2, 1] }}
              />
            </div>
          </Reveal>

          {/* CONTENT — right column */}
          <div className="g33-module-hero-content">
            <Reveal delay={0.1}>
              <div className="g33-module-hero-eyebrow">
                <span className="g33-module-hero-eyebrow-rule" />
                Day {m.day} · {dayLabels[m.day]}
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <h1 className="g33-display g33-module-hero-title" data-testid="module-title">
                {m.title}
              </h1>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="g33-module-hero-hook" data-testid="module-hook">
                {m.hook}
              </p>
            </Reveal>

            {/* STAT BOXES */}
            <Reveal delay={0.45}>
              <div className="g33-module-stat-grid" data-testid="module-stats">
                <div className="g33-module-stat-box">
                  <div className="g33-module-stat-value">
                    {String(m.module_number).padStart(2, '0')}
                  </div>
                  <div className="g33-module-stat-label">Module</div>
                </div>
                <div className="g33-module-stat-box">
                  <div className="g33-module-stat-value">{m.duration_min}<span style={{ fontSize: '0.42em', marginLeft: 6, fontWeight: 400 }}>min</span></div>
                  <div className="g33-module-stat-label">Duration</div>
                </div>
                <div className="g33-module-stat-box">
                  <div className="g33-module-stat-value">{m.day}<span style={{ fontSize: '0.42em', marginLeft: 6, fontWeight: 400 }}>/3</span></div>
                  <div className="g33-module-stat-label">Day</div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.6}>
              <a href="#objective" className="g33-btn-primary g33-module-hero-cta" data-testid="module-cta">
                Discover the module <ArrowRight size={14} />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OBJECTIVE — TRAINER VOICE section */}
      <section id="objective" style={{ padding: '120px 32px', maxWidth: 1080, margin: '0 auto' }}>
        <Reveal>
          <TrainerSection eyebrow="Objective" testId="objective-trainer">
            <p className="g33-body" style={{ margin: 0 }}>
              {m.objective}
            </p>
          </TrainerSection>
        </Reveal>
      </section>

      {/* TRAINER NARRATIVE */}
      <section style={{ background: 'var(--g33-cream-soft)', padding: '120px 32px' }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <Reveal>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              fontSize: 11, fontWeight: 500, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: 'var(--g33-or)',
              marginBottom: 24,
            }}>
              <span className="g33-trainer-tag-dot" aria-hidden="true" />
              Trainer voice · Nicolas
            </div>
            <div className="g33-eyebrow" style={{ marginBottom: 24, color: 'var(--g33-or-deep)' }}>
              Trainer narrative
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="module-narrative-rendered">
              {renderNarrative(m.trainer_narrative)}
            </div>
          </Reveal>
          {/* Aircraft fleet gallery — Module 11 only */}
          {m.slug === 'd3-m11-aircraft-familiarisation' && (
            <Reveal delay={0.15}>
              <div style={{ marginTop: 72 }}>
                <div className="g33-eyebrow" style={{ marginBottom: 24, color: 'var(--g33-or-deep)' }}>
                  The fleet — line drawings
                </div>
                <AircraftFleetGallery />
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* PULL QUOTE */}
      {m.pull_quote && (
        <section style={{ padding: '120px 32px' }}>
          <div style={{ maxWidth: 1080, margin: '0 auto' }}>
            <Reveal>
              <blockquote className="g33-pull-quote" data-testid="module-pullquote">
                {m.pull_quote}
              </blockquote>
            </Reveal>
            {m.pull_quote_followup && (
              <Reveal delay={0.15}>
                <div className="g33-followup" data-testid="pullquote-followup">
                  {m.pull_quote_followup.eyebrow && (
                    <div className="g33-eyebrow" style={{ marginBottom: 32, color: 'var(--g33-or-deep)' }}>
                      {m.pull_quote_followup.eyebrow}
                    </div>
                  )}
                  <div className="g33-followup-grid">
                    {(m.pull_quote_followup.blocks || []).map((b, i) => (
                      <div key={i} className="g33-followup-block">
                        <h3 className="g33-display g33-followup-title">{b.title}</h3>
                        <p className="g33-body" style={{ margin: 0 }}>{b.body}</p>
                      </div>
                    ))}
                  </div>
                  {m.pull_quote_followup.footer_title && (
                    <div className="g33-followup-footer">
                      <div className="g33-eyebrow" style={{ marginBottom: 12, color: 'var(--g33-or)' }}>
                        {m.pull_quote_followup.footer_title}
                      </div>
                      <p className="g33-body" style={{ margin: 0 }}>{m.pull_quote_followup.footer_body}</p>
                    </div>
                  )}
                </div>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* DEMONSTRATION — TRAINER VOICE section */}
      {m.demonstration && (
        <section style={{ padding: '0 32px 120px', maxWidth: 1080, margin: '0 auto' }}>
          <Reveal>
            <TrainerSection eyebrow="Demonstration" testId="demonstration-trainer">
              {m.demonstration.intro && (
                <div style={{
                  marginBottom: 32,
                  paddingBottom: 24,
                  borderBottom: '1px solid rgba(201, 169, 97, 0.25)',
                  fontSize: 16,
                  lineHeight: 1.7,
                  color: 'var(--g33-charcoal)',
                  whiteSpace: 'pre-line',
                }}>
                  {withCodes(m.demonstration.intro, (t) => t)}
                </div>
              )}
              {m.demonstration.scenario && (
                <>
                  <div className="g33-eyebrow" style={{ marginBottom: 12, color: 'var(--g33-or-deep)' }}>
                    Scenario
                  </div>
                  <div style={{ maxWidth: 760 }} data-testid="demonstration-scenario">
                    {withCodes(m.demonstration.scenario, renderScenario)}
                  </div>
                </>
              )}
            </TrainerSection>
          </Reveal>
        </section>
      )}

      {/* PARTICIPANT INTERACTION */}
      {m.participant_interaction && (
        <section style={{ padding: '0 32px 120px', maxWidth: 1080, margin: '0 auto' }}>
          <Reveal>
            <div className="g33-eyebrow" style={{ marginBottom: 24 }}>Interaction</div>
            <div style={{ maxWidth: 760 }} data-testid="module-interaction">
              {renderRichText(m.participant_interaction, { baseFontSize: 17 })}
            </div>
          </Reveal>
        </section>
      )}

      {/* OPERATIONAL INSIGHT — TRAINER VOICE section (dark variant) */}
      {m.operational_insight && (
        <section style={{ padding: '0 32px 120px', maxWidth: 1080, margin: '0 auto' }}>
          <Reveal>
            <TrainerSection eyebrow="Operational insight" tone="dark" testId="insight-trainer">
              {withCodes(m.operational_insight, (t) => (
                <p className="g33-insight-body" style={{ margin: 0 }}>{t}</p>
              ))}
            </TrainerSection>
          </Reveal>
        </section>
      )}

      <ReadingList items={m.digital_companion?.reading_list} />

      {/* RETURN TO DAY OVERVIEW */}
      <section style={{ padding: '80px 32px 160px', textAlign: 'center' }}>
        <Link to={`/gate33-program/day/${m.day}`} className="g33-btn-ghost">
          ← Day {m.day} overview
        </Link>
      </section>

      <BottomDock current={m} all={allSorted} />
    </>
  );
};
